import { PortalDashboardShell } from "../components/PortalDashboardShell";
import { PortalTabHeader } from "../components/PortalTabHeader";
import { FirstLoginWelcomePanel } from "../components/FirstLoginWelcomePanel";
import { BrokerOnly } from "../components/BrokerOnly";
import { usePortalAuth } from "../PortalAuthContext";
import { RequestsTab } from "../tabs/RequestsTab";
import { DocumentsTab } from "../tabs/DocumentsTab";
import { BrokerDealsDashboard } from "./BrokerDealsPage";
import { FilesTab } from "../tabs/FilesTab";
import { AccountSettingsTab } from "../tabs/AccountSettingsTab";
import { BrokerHomeTab } from "../tabs/BrokerHomeTab";
import { RecipientTaxFormsTab } from "../tabs/RecipientTaxFormsTab";
import { BrokerCommissionsTab } from "../tabs/BrokerCommissionsTab";
import { BrokerPaymentSetupTab } from "../tabs/BrokerPaymentSetupTab";
import { NotificationsTab } from "../tabs/NotificationsTab";
import { BrokerAffiliationsTab } from "../tabs/BrokerAffiliationsTab";
import { PrequalificationTab } from "../tabs/PrequalificationTab";
import { CommissionModelerTab } from "../tabs/CommissionModelerTab";
import type { PortalTabId } from "../components/PortalTabMatrix";

// External Portal v2 Phase 3.1 — broker home renders inside
// PortalDashboardShell so brokers get the same Requests / Documents /
// Account & settings tabs (plus the Phase 3 placeholders) as recipients.
//
// Portal Round-3 #1 / #2 — the welcome banner, "Application status"
// line, application checklist, onboarding stepper, "what's next" CTAs,
// and the in-portal "Set a password" section were deleted. They lived
// in the shell's `header` slot, which rendered above every tab ("tab
// bleed"); each tab now renders only its own content. Broker
// certification status is surfaced by the BrokerStatusBadge that
// PortalDashboardShell renders for broker accounts (Round-3 #10).

export function BrokerDashboardPage() {
  const auth = usePortalAuth();
  return (
    <BrokerOnly>
      <PortalDashboardShell
        pageTitle="Broker dashboard"
        renderTab={(activeTab) => (
          <>
            {/* §7 — shared page header on every tab; first-login welcome
                panel on the Requests (landing) tab only. The Deals tab now
                hosts the broker's submissions dashboard (with the "Submit a
                new deal" entry point), so its subtitle is no longer the
                recipient-style read-only line. */}
            <PortalTabHeader
              accountType="Broker"
              tab={activeTab}
              subtitle={
                activeTab === "deals"
                  ? "Submit a new deal and track where each of your submissions stands."
                  : undefined
              }
            />
            {/* Self-apply follow-ups — suppress the inline first-login panel
                for self-applicants so they don't see two welcomes; the
                "Application received" modal (PortalChrome) is their welcome. */}
            {activeTab === "requests" && !auth.me?.showApplicationWelcome && (
              <FirstLoginWelcomePanel />
            )}
            {renderBrokerTabBody(activeTab)}
          </>
        )}
      />
    </BrokerOnly>
  );
}

function renderBrokerTabBody(activeTab: PortalTabId) {
  switch (activeTab) {
    // Journey-Stage Adaptive Dashboards — Home is the broker's default landing.
    case "home":
      return <BrokerHomeTab />;
    case "requests":
      return <RequestsTab />;
    // Brokerage / Sub-Broker PR-7 — the broker-side affiliation consent surface.
    case "affiliations":
      return <BrokerAffiliationsTab />;
    case "documents":
      return <DocumentsTab />;
    case "settings":
      return <AccountSettingsTab />;
    case "payments":
      // External party portals PR-C — the broker's Commissions view (total
      // earned + per-loan breakdown + payout history over the recognized
      // waterfall broker carve).
      return <BrokerCommissionsTab />;
    // Payment Setup — the broker's guided Stripe payout-account onboarding.
    case "payment-setup":
      return <BrokerPaymentSetupTab />;
    case "notifications":
      return <NotificationsTab />;
    // Journey-Stage Adaptive Dashboards (defect fold-in) — the broker's Tax
    // forms tab is in the broker lineup (PortalTabMatrix) but had no case, so
    // it rendered blank. The broker is a 1099 recipient; reuse the shared
    // read-only "my forms" view.
    case "tax-forms":
      return <RecipientTaxFormsTab />;
    case "deals":
      // fix-broker-portal-submit-deal-entrypoint — the broker's Deals tab
      // now renders their own submissions dashboard (drafts + submitted +
      // staging, with the "Submit a new deal" entry point), not the
      // read-only counterparty-deals mirror recipients see.
      return <BrokerDealsDashboard />;
    // Partner Brand Ownership — the completions this broker's own (or their
    // firm's inherited) prequalification site produced.
    case "prequalification":
      return <PrequalificationTab />;
    case "files":
      return <FilesTab />;
    // Broker Commission Modeler — the broker's private what-if scratchpad.
    case "commission-modeler":
      return <CommissionModelerTab />;
    case "contributions":
    case "invoices":
      return null;
    // 🔴 This switch is NOT exhaustiveness-checked — PortalTabId has ~30
    // members and only a dozen are in the broker lineup, so TypeScript cannot
    // tell a deliberate omission from a forgotten one. The failure mode is
    // silent and has already shipped once (see the `tax-forms` note above): the
    // tab appears in the rail, PortalTabHeader draws a correct heading over it,
    // and the body is BLANK. An explicit `default` does not prevent that, but it
    // at least makes the fall-through a decision someone wrote down rather than
    // an implicit `undefined`. Adding a tab to the Broker lineup in
    // PortalTabMatrix means adding a case HERE too.
    default:
      return null;
  }
}
